import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Plus, ShieldCheck, ShieldAlert } from "lucide-react";
import { hostService } from "@/lib/services";
import type { Property } from "@/types/api";

export const Route = createFileRoute("/_authenticated/host/properties/")({
  component: HostPropertiesList,
});

function HostPropertiesList() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["host-properties"],
    queryFn: () => hostService.properties(),
  });
  const items = (data && (data as { data?: Property[] }).data) ?? [];

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-ink">My properties</h1>
          <p className="mt-1 text-sm text-ink-muted">
            Properties you own. Add a new one to expand your stays.
          </p>
        </div>
        <Link
          to="/host/properties/new"
          className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground hover:opacity-90"
        >
          <Plus className="h-4 w-4" /> New property
        </Link>
      </div>

      {isLoading && (
        <div className="mt-6 space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="h-24 animate-pulse rounded-2xl border border-border brand-skeleton"
            />
          ))}
        </div>
      )}

      {isError && (
        <div className="mt-6 rounded-2xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
          {(error as Error).message}
        </div>
      )}

      {!isLoading && items.length === 0 && (
        <div className="mt-10 rounded-3xl border border-border bg-card p-10 text-center">
          <p className="text-base font-bold text-ink">No properties yet</p>
          <p className="mt-1 text-sm text-ink-muted">
            Add your first property to start hosting on CavaYo.
          </p>
        </div>
      )}

      <ul className="mt-6 space-y-3">
        {items.map((p) => (
          <li key={p.id}>
            <Link
              to="/properties/$propertyId"
              params={{ propertyId: p.id }}
              className="flex items-center gap-4 rounded-2xl border border-border bg-card p-4 transition hover:-translate-y-0.5 hover:shadow-[var(--shadow-card)]"
            >
              <div className="h-16 w-20 shrink-0 overflow-hidden rounded-xl bg-muted">
                {p.cover_image ? (
                  <img src={p.cover_image} alt="" className="h-full w-full object-cover" />
                ) : null}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-extrabold text-ink">{p.name}</p>
                <p className="truncate text-xs text-ink-muted">{p.address}</p>
                <p className="mt-1 text-[11px] uppercase tracking-wider text-ink-subtle">
                  {p.listings?.length ?? 0} listings
                </p>
              </div>
              <span
                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider ${
                  p.verified ? "bg-primary/15 text-primary" : "bg-accent/40 text-secondary"
                }`}
              >
                {p.verified ? (
                  <ShieldCheck className="h-3 w-3" />
                ) : (
                  <ShieldAlert className="h-3 w-3" />
                )}
                {p.verified ? "Verified" : "Pending"}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
